import { Button } from "@mui/material";
import { type FC, useCallback } from "react";
import { type FieldErrors, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import type { FollowUpMomentDetails } from "../../../follow-up/server/types";
import { ConditionalTooltip } from "../../../generic/components/conditional-tooltip";
import { useMomentFormValues } from "../../hooks/use-moment-form-values";
import type { MomentRestrictions } from "../../hooks/use-moment-restrictions";
import type { MomentFormYupContext } from "../../types";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { FollowUpsPublishDialog } from "./follow-ups-publish-dialog";
import { usePublishAction } from "./hooks/use-publish-action";
import { usePublishPermissions } from "./hooks/use-publish-permissions";
import { PublishDialog } from "./publish-dialog";
import type { SaveButtonProps } from "./types";
import { toastErrors } from "./utils";

type PublishButtonProps = SaveButtonProps & {
  momentRestrictions: MomentRestrictions;
  review: { value: true; open: (data: { isReview: boolean }) => void } | { value: false };
  closePreviewDialog: () => void;
};

export const PublishButton: FC<PublishButtonProps> = ({
  formSaveState,
  handleSave,
  saveDataReady,
  momentRestrictions,
  review,
  closePreviewDialog,
}) => {
  const {
    saving,
    setSaving,
    formDisabled: { "publish-button": disabled },
    setLastAction,
  } = formSaveState;

  const {
    handleOpen: openPublishDialog,
    open: publishDialogOpen,
    setOpen: setPublishDialogOpen,
  } = useDialog();

  const {
    handleOpen: openFollowUpsDialog,
    open: followUpsDialogOpen,
    handleClose: followUpsDialogClose,
    data: followUpMomentDetails,
  } = useDialog<FollowUpMomentDetails>();

  const { handleSubmit, trigger, getFieldState } = useFormContext<
    MomentFormMoment,
    MomentFormYupContext
  >();

  const { schedule } = useMomentFormValues(["schedule"]);

  const { isRestricted, loading: loadingUsesRestrictedData } =
    usePublishPermissions(momentRestrictions);

  const { executePublish } = usePublishAction(handleSave);

  const onPublish = useCallback(
    async (values: MomentFormMoment) => {
      setSaving(true);
      const { success, error } = await executePublish(values);

      if (success) {
        review.value ? review.open({ isReview: true }) : openPublishDialog();
      } else {
        if (error) toast.error(error);
      }
      setSaving(false);
    },
    [executePublish, setSaving, openPublishDialog, review],
  );

  const onError = useCallback(
    async (errs: FieldErrors<MomentFormMoment>) => {
      await trigger();
      toastErrors(errs);
      setSaving(false);
      if (getFieldState("schedule").error) toast.error("Schedule is not valid");
    },
    [trigger, setSaving, getFieldState],
  );

  return (
    <ConditionalTooltip
      arrow
      situations={[
        {
          title: "You don’t have permission to send with this sender or segment.",
          condition: isRestricted,
        },
      ]}
    >
      <span>
        <Button
          id={review.value ? "previewAndPublishMomentButton" : "publishMomentButton"}
          data-analytics-id={
            review.value ? "moment-form-preview-and-publish" : "moment-form-publish"
          }
          variant="contained"
          sx={{ ml: 2 }}
          loading={loadingUsesRestrictedData || saving}
          disabled={disabled || isRestricted || !saveDataReady}
          onClick={() => {
            setLastAction(review.value ? "draft" : "publish");
            void handleSubmit(onPublish, onError)();
          }}
        >
          {review.value ? "Review & Publish" : "Publish Communication"}
        </Button>
        <PublishDialog
          open={publishDialogOpen}
          setOpen={setPublishDialogOpen}
          setSaving={setSaving}
          schedule={schedule}
          onPublished={(data) => {
            closePreviewDialog();

            data && openFollowUpsDialog(data);
          }}
        />

        {followUpMomentDetails && (
          <FollowUpsPublishDialog
            open={followUpsDialogOpen}
            onClose={followUpsDialogClose}
            followUpData={followUpMomentDetails}
          />
        )}
      </span>
    </ConditionalTooltip>
  );
};
